<template>
  <main class="app-content">
    <div class="app-title">
      <div>
        <h1><font-awesome-icon icon="map-marker-alt" /> {{ $t('geoAddressTitle') }}</h1>
        <p class="text-muted mb-0">{{ $t('geoAddressDesc') }}</p>
      </div>
    </div>

    <div class="row">
      <!-- Tóm tắt usage tháng này (theo dõi count) -->
      <div class="col-12" v-if="usage">
        <div class="tile">
          <h4 class="tile-title mb-2">{{ $t('geoUsageTitle', [usage.yearMonth]) }}</h4>
          <div v-for="(u, prov) in usage.providers" :key="prov" class="d-flex flex-wrap align-items-center mb-1" style="gap: 22px;">
            <div style="min-width: 68px"><b class="text-uppercase">{{ prov }}</b></div>
            <div><span class="text-muted small">{{ $t('geoPlaceDetail') }}:</span> <strong>{{ u.placeDetail }}</strong><span v-if="u.freeQuota" class="text-muted small"> / {{ u.freeQuota }} free</span></div>
            <div><span class="text-muted small">{{ $t('geoAutocomplete') }}:</span> <strong>{{ u.autocomplete }}</strong></div>
            <div><span class="text-muted small">{{ $t('geoEstCost') }}:</span> <strong :class="u.estCostVnd > 0 ? 'text-danger' : ''">{{ (u.estCostVnd || 0).toLocaleString('vi-VN') }} đ</strong></div>
          </div>
          <div v-if="!Object.keys(usage.providers || {}).length" class="text-muted small">{{ $t('geoNoUsage') }}</div>
        </div>
      </div>

      <div class="col-12">
        <div class="tile">
          <div class="form-inline mb-2">
            <b-form-input class="mr-1 mb-1" v-model="filterFormModel.keyword" v-on:keydown.enter="searchNow"
                          :placeholder="$t('geoAddressKeyword')" style="min-width: 300px"></b-form-input>
            <form-select :model="filterFormModel" attribute="provider" class="mr-1 mb-1" :options="[
              {text: $t('all'), value: null},
              {text: 'Google', value: 'google'},
              {text: 'Goong', value: 'goong'},
            ]"></form-select>
            <button @click="searchNow" class="btn btn-primary mr-1 mb-1">{{ $t('search') }}</button>
            <button @click="resetFilter" class="btn btn-secondary mr-1 mb-1">Reset</button>
          </div>
        </div>
      </div>

      <div class="col-12">
        <div class="c-paging clearfix">
          <div class="c-paging__count" v-if="itemCount">{{ $t('found', [itemCount]) }}</div>
          <div class="c-paging__right">
            <b-pagination v-if="itemCount > filterFormModel.pageSize" class="pull-right" size="sm"
                          v-model="filterFormModel.page" :total-rows="itemCount" :per-page="filterFormModel.pageSize"
                          @input="load"></b-pagination>
          </div>
        </div>
      </div>

      <div class="col-12">
        <div class="tile">
          <div class="table-responsive">
            <table class="table table-hover mb-0">
              <thead>
                <tr>
                  <th>{{ $t('address') }}</th>
                  <th>{{ $t('geoProvider') }}</th>
                  <th>{{ $t('geoCoords') }}</th>
                  <th class="text-right">{{ $t('geoHitCount') }}</th>
                  <th>{{ $t('geoLastUsed') }}</th>
                  <th>{{ $t('geoExpire') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!items.length && !loading">
                  <td colspan="6" class="text-center text-muted py-3">{{ $t('geoNoData') }}</td>
                </tr>
                <tr v-for="item in items" :key="item._id">
                  <td>{{ item.formattedAddress || item.rawAddress || '—' }}</td>
                  <td><span class="badge badge-light text-uppercase">{{ item.provider }}</span></td>
                  <td class="text-monospace small">{{ item.lat != null ? (item.lat + ', ' + item.lng) : '—' }}</td>
                  <td class="text-right">{{ item.hitCount || 0 }}</td>
                  <td><span v-if="item.lastUsedTime">{{ item.lastUsedTime | moment('DD/MM/YYYY HH:mm') }}</span><span v-else>—</span></td>
                  <td>
                    <span v-if="item.latLngExpireAt" :class="new Date(item.latLngExpireAt) < new Date() ? 'text-danger' : 'text-muted'" class="small">
                      {{ item.latLngExpireAt | moment('DD/MM/YYYY') }}
                    </span>
                    <span v-else class="text-muted small">{{ $t('geoNoExpire') }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>
<script>
import List from '@/mixins/List';

export default {
  mixins: [List],
  data() {
    return {
      autoLoad: true,
      service: '/geo-address/list',
      usage: null,
      defaultFilterFormModel: {
        page: 1,
        pageSize: 20,
        keyword: null,
        provider: null,
      },
    };
  },
  methods: {
    loadUsage() {
      this.$service.get('/geocode-usage').then(r => { this.usage = r.data; }).catch(e => console.warn('loadUsage', e && e.message));
    },
    searchNow() {
      this.filterFormModel.page = 1;
      this.load();
    },
    resetFilter() {
      this.reset();
      this.load();
    },
  },
  mounted() {
    this.loadUsage();
  },
  i18n: {
    messages: {
      en: {
        geoAddressTitle: 'Address dictionary',
        geoAddressDesc: 'Addresses cached from geocode suggestions (Google/Goong). Reused entries avoid new API calls.',
        geoAddressKeyword: 'Search by address',
        geoUsageTitle: 'Geocode usage this month ({0}) — system-wide (shared key)',
        geoPlaceDetail: 'Place Details (billable)',
        geoAutocomplete: 'Autocomplete',
        geoEstCost: 'Est. cost',
        geoNoUsage: 'No calls this month.',
        geoProvider: 'Provider',
        geoCoords: 'Coordinates',
        geoHitCount: 'Reused',
        geoLastUsed: 'Last used',
        geoExpire: 'lat/lng expiry',
        geoNoExpire: 'permanent',
        geoNoData: 'No cached address yet.',
        address: 'Address',
      },
      vi: {
        geoAddressTitle: 'Từ điển địa chỉ',
        geoAddressDesc: 'Địa chỉ đã cache từ gợi ý (Google/Goong). Địa chỉ dùng lại thì KHÔNG gọi API mới (tiết kiệm).',
        geoAddressKeyword: 'Tìm theo địa chỉ',
        geoUsageTitle: 'Lượt dùng tháng này ({0}) — toàn hệ thống (key chung)',
        geoPlaceDetail: 'Place Details (tính tiền)',
        geoAutocomplete: 'Gợi ý',
        geoEstCost: 'Ước chi phí',
        geoNoUsage: 'Chưa có lượt gọi tháng này.',
        geoProvider: 'Nguồn',
        geoCoords: 'Toạ độ',
        geoHitCount: 'Số lần dùng lại',
        geoLastUsed: 'Dùng lần cuối',
        geoExpire: 'Hạn lat/lng',
        geoNoExpire: 'vĩnh viễn',
        geoNoData: 'Chưa có địa chỉ nào được cache.',
        address: 'Địa chỉ',
      },
    },
  },
};
</script>
